import { useState } from 'react'
import {
  Plus,
  Ticket,
  Pencil,
  Power,
  Copy,
  Check,
  Loader2,
  Infinity as InfinityIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useAdminCoupons,
  useCreateCoupon,
  useUpdateCoupon,
  useDeactivateCoupon,
} from '@/services/admin/coupons.service'
import type { Coupon, CreateCouponDTO, UpdateCouponDTO } from '@/services/admin/coupons.service'
import { useCompanies } from '@/services/admin/companies.service'
import { useAdminCourses } from '@/services/admin/courses.service'
import { useToast } from '@/hooks/use-toast'
import { getErrorMessage } from '@/utils/error'

interface CouponForm {
  code: string
  companyId: string
  allCourses: boolean
  courseIds: number[]
  maxUses: string
  unlimited: boolean
  expiresAt: string
}

const emptyForm = (): CouponForm => ({
  code: '',
  companyId: '',
  allCourses: false,
  courseIds: [],
  maxUses: '',
  unlimited: false,
  expiresAt: '',
})

export default function CouponsManagement() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null)
  const [deactivateConfirmId, setDeactivateConfirmId] = useState<string | null>(null)
  const [form, setForm] = useState<CouponForm>(emptyForm())
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const { toast } = useToast()

  const { data: coupons, isLoading } = useAdminCoupons()
  const { data: companies } = useCompanies()
  const { data: courses } = useAdminCourses()
  const createCoupon = useCreateCoupon()
  const updateCoupon = useUpdateCoupon()
  const deactivateCoupon = useDeactivateCoupon()

  const companyCourses =
    courses?.filter((c) => c.companyId === form.companyId && c.status === 'published') ?? []

  const openCreate = () => {
    setEditingCoupon(null)
    setForm(emptyForm())
    setDialogOpen(true)
  }

  const openEdit = (coupon: Coupon) => {
    setEditingCoupon(coupon)
    setForm({
      code: coupon.code,
      companyId: coupon.companyId,
      allCourses: coupon.allCourses,
      courseIds: coupon.coupon_courses.map((cc) => cc.courseId),
      maxUses: coupon.maxUses?.toString() ?? '',
      unlimited: coupon.maxUses === null,
      expiresAt: coupon.expiresAt.slice(0, 16),
    })
    setDialogOpen(true)
  }

  const handleCancel = () => {
    setForm(emptyForm())
    setEditingCoupon(null)
    setDialogOpen(false)
  }

  const handleSubmit = async () => {
    try {
      if (editingCoupon) {
        const dto: UpdateCouponDTO & { id: string } = {
          id: editingCoupon.id,
          code: form.code,
          companyId: form.companyId,
          allCourses: form.allCourses,
          courseIds: form.allCourses ? [] : form.courseIds,
          maxUses: form.unlimited ? null : Number(form.maxUses),
          expiresAt: new Date(form.expiresAt).toISOString(),
        }
        await updateCoupon.mutateAsync(dto)
        toast({ title: 'Cupom atualizado!' })
      } else {
        const dto: CreateCouponDTO = {
          code: form.code,
          companyId: form.companyId,
          allCourses: form.allCourses,
          courseIds: form.allCourses ? undefined : form.courseIds,
          maxUses: form.unlimited ? null : Number(form.maxUses),
          expiresAt: new Date(form.expiresAt).toISOString(),
        }
        await createCoupon.mutateAsync(dto)
        toast({ title: 'Cupom criado!' })
      }
      handleCancel()
    } catch (error: unknown) {
      toast({
        title: 'Erro',
        description: getErrorMessage(error),
        variant: 'destructive',
      })
    }
  }

  const handleDeactivate = async (id: string) => {
    await deactivateCoupon.mutateAsync(id)
    setDeactivateConfirmId(null)
    toast({ title: 'Cupom desativado' })
  }

  const copyLink = (code: string, id: string) => {
    const baseUrl = window.location.origin
    navigator.clipboard.writeText(`${baseUrl}/signup?cupom=${code}`)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
    toast({ title: 'Link copiado!' })
  }

  const isExpired = (date: string) => new Date(date) < new Date()
  const isExhausted = (coupon: Coupon) =>
    coupon.maxUses !== null && coupon.usedCount >= coupon.maxUses

  const getStatusBadge = (coupon: Coupon) => {
    if (!coupon.active) return <Badge variant="outline">Inativo</Badge>
    if (isExpired(coupon.expiresAt)) return <Badge variant="destructive">Expirado</Badge>
    if (isExhausted(coupon)) return <Badge variant="secondary">Esgotado</Badge>
    return <Badge variant="default">Ativo</Badge>
  }

  const isMutating = createCoupon.isPending || updateCoupon.isPending
  const isFormValid =
    form.code.trim() &&
    form.companyId &&
    form.expiresAt &&
    (form.unlimited || (form.maxUses && Number(form.maxUses) > 0)) &&
    (form.allCourses || form.courseIds.length > 0)

  const handleCourseToggle = (courseId: number) => {
    setForm((prev) => ({
      ...prev,
      courseIds: prev.courseIds.includes(courseId)
        ? prev.courseIds.filter((id) => id !== courseId)
        : [...prev.courseIds, courseId],
    }))
  }

  return (
    <>
      <Card className="glass-card border-border/50">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-primary" />
              Códigos
            </CardTitle>
            <CardDescription>
              Gerencie códigos de cadastro para vincular usuários a empresas e cursos
            </CardDescription>
          </div>
          <Button onClick={openCreate} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Novo Cupom
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Carregando cupons...
            </div>
          ) : !coupons || coupons.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum cupom cadastrado ainda.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Cursos</TableHead>
                  <TableHead>Uso</TableHead>
                  <TableHead>Expira em</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-32">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coupons.map((coupon) => (
                  <TableRow key={coupon.id}>
                    <TableCell className="font-mono font-medium">{coupon.code}</TableCell>
                    <TableCell className="text-sm">{coupon.company.name}</TableCell>
                    <TableCell className="text-sm">
                      {coupon.allCourses ? (
                        <Badge variant="secondary">Todos os cursos</Badge>
                      ) : (
                        <span className="text-muted-foreground">
                          {coupon.coupon_courses.length} curso
                          {coupon.coupon_courses.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {coupon.maxUses === null ? (
                        <span className="flex items-center gap-1">
                          {coupon.usedCount} / <InfinityIcon className="w-4 h-4" />
                        </span>
                      ) : (
                        `${coupon.usedCount} / ${coupon.maxUses}`
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(coupon.expiresAt).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell>{getStatusBadge(coupon)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => copyLink(coupon.code, coupon.id)}
                          title="Copiar link"
                        >
                          {copiedId === coupon.id ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(coupon)}
                          title="Editar"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {coupon.active && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeactivateConfirmId(coupon.id)}
                            title="Desativar"
                            className="text-destructive hover:text-destructive"
                          >
                            <Power className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-primary" />
              {editingCoupon ? 'Editar Cupom' : 'Novo Cupom'}
            </DialogTitle>
            <DialogDescription>
              {editingCoupon
                ? 'Atualize as informações do cupom.'
                : 'Configure o cupom de cadastro.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="coupon-code">Código do Cupom</Label>
              <Input
                id="coupon-code"
                placeholder="Ex: FCAMARA2026"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label>Empresa</Label>
              <Select
                value={form.companyId}
                onValueChange={(v) => setForm({ ...form, companyId: v, courseIds: [] })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies?.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="all-courses"
                checked={form.allCourses}
                onCheckedChange={(checked) =>
                  setForm({ ...form, allCourses: !!checked, courseIds: [] })
                }
              />
              <Label htmlFor="all-courses" className="cursor-pointer">
                Todos os cursos da empresa (inclui futuros)
              </Label>
            </div>

            {!form.allCourses && form.companyId && (
              <div className="space-y-2">
                <Label>Cursos</Label>
                {companyCourses.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Nenhum curso encontrado para esta empresa.
                  </p>
                ) : (
                  <div className="max-h-40 overflow-y-auto space-y-2 border rounded-md p-3">
                    {companyCourses.map((course) => (
                      <div key={course.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`course-${course.id}`}
                          checked={form.courseIds.includes(course.id)}
                          onCheckedChange={() => handleCourseToggle(course.id)}
                        />
                        <Label htmlFor={`course-${course.id}`} className="cursor-pointer text-sm">
                          {course.title}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="unlimited"
                  checked={form.unlimited}
                  onCheckedChange={(checked) =>
                    setForm({ ...form, unlimited: !!checked, maxUses: '' })
                  }
                />
                <Label htmlFor="unlimited" className="cursor-pointer">
                  Uso ilimitado
                </Label>
              </div>
              {!form.unlimited && (
                <div className="space-y-1">
                  <Label htmlFor="max-uses">Limite de usos</Label>
                  <Input
                    id="max-uses"
                    type="number"
                    min="1"
                    placeholder="Ex: 100"
                    value={form.maxUses}
                    onChange={(e) => setForm({ ...form, maxUses: e.target.value })}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="expires-at">Data de expiração</Label>
              <Input
                id="expires-at"
                type="datetime-local"
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={!isFormValid || isMutating}>
              {isMutating ? 'Salvando...' : editingCoupon ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={deactivateConfirmId !== null} onOpenChange={() => setDeactivateConfirmId(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Desativar Cupom</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja desativar este cupom? Ele não poderá mais ser usado para novos
              cadastros.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeactivateConfirmId(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deactivateConfirmId && handleDeactivate(deactivateConfirmId)}
              disabled={deactivateCoupon.isPending}
            >
              {deactivateCoupon.isPending ? 'Desativando...' : 'Desativar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
